/**
 * ── A BLOCK THAT DECIDES AT SEND TIME SHOWS A PLACEHOLDER IN THE HUB (2026-10-15) ──────
 *
 * Layne's national promo banner (ASK-JACK-NSE-PROMO-BANNER-20261015) picks its image with
 * AMPscript at send: `%%[ … lookup by send date … ]%%` and `<img src="%%=v(@img)=%%">`.
 * A browser can't run that, so the canvas, the preview rail and the review pane after
 * Done painted the script as stray text plus a broken image. Jack approved the mockup
 * `_mockups/NSE-PROMO-PLACEHOLDER-20261015.html`: a dashed grey box naming the block.
 *
 * DISPLAY ONLY. What reaches SFMC is the block exactly as Layne wrote it — no caller on
 * the submit path imports this file. The swap is exact-substring surgery on the block's
 * own rendered row (the same `render()` the document uses), so there is no parsing and
 * nothing else in the email can move.
 *
 * WHICH BLOCKS: any block whose rendered markup holds AMPscript (`%%[` or `%%=`). Plain
 * personalization (`%%FirstName%%`) does not count — it is not a send-time decision and
 * a stray token in preview is the long-standing behaviour.
 *
 * WORDING: line 1 is the block's own label; line 2 is the block's `sendTimeNote` when its
 * guardrails declare one, else the approved default. Both come from Layne's block, so a
 * later send-time block can say its own thing without an app change.
 */
import { blockDef } from "../email/blocks";
import { escML } from "../email/core";
import { echMeta } from "./echRegistry";

export const SENDTIME_DEFAULT_NOTE = "Picked at send from the promo schedule";

type BlockLike = { type: string; data?: unknown };

const AMP_RE = /%%\[|%%=/;

/** true when a block's markup runs AMPscript at send */
export function isSendTime(html: string): boolean {
  return AMP_RE.test(String(html || ""));
}

/** "ECH_NationalPromo" → "National Promo"; a real label passes through */
export function sendTimeLabel(type: string): string {
  const def = blockDef(type);
  const raw = String((def && def.label) || "").trim();
  if (!raw) return "Send-time block";
  if (!/^ECH_/i.test(raw)) return raw;
  return raw.replace(/^ECH_/i, "").replace(/_/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim() || raw;
}

function noteFor(type: string): string {
  const m = echMeta(type);
  const full = (m && m.full) as Record<string, unknown> | null;
  const n = full && typeof full.sendTimeNote === "string" ? full.sendTimeNote.trim() : "";
  return n || SENDTIME_DEFAULT_NOTE;
}

/** the placeholder row — same `<tr><td style="padding:0">` shape every ECH block renders */
export function sendTimePlaceholderRow(type: string): string {
  return '<tr><td style="padding:0" data-sendtime-ph="1">' +
    '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse"><tr>' +
    '<td align="center" valign="middle" height="96" style="height:96px;background:#eef1f4;border:1.5px dashed #9aabbd;text-align:center;font-family:Inter,-apple-system,\'Segoe UI\',Arial,sans-serif">' +
    '<div style="font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#1B2A58">' + escML(sendTimeLabel(type)) + "</div>" +
    '<div style="font-size:11.5px;color:#6b8199;margin-top:4px">' + escML(noteFor(type)) + "</div>" +
    "</td></tr></table></td></tr>";
}

function rowOfBlock(b: BlockLike): string {
  const def = blockDef(b.type);
  if (!def) return "";
  try { return (def.render as (d: unknown) => string)(b.data); } catch { return ""; }
}

/** one block's row for the canvas: the placeholder when it runs AMPscript, else unchanged */
export function sendTimeViewRow(type: string, row: string): string {
  return isSendTime(row) ? sendTimePlaceholderRow(type) : row;
}

/**
 * A whole document for display: every send-time block's row is swapped for its
 * placeholder. `blocks` must be the list the html was rendered from; a row that can't be
 * found verbatim (hand-edited html, a different doc) is left alone rather than guessed at.
 */
export function sendTimeView(html: string, blocks: ReadonlyArray<BlockLike> | null | undefined): string {
  let out = String(html || "");
  if (!out || !isSendTime(out) || !blocks || !blocks.length) return out;
  for (const b of blocks) {
    if (!b || !b.type) continue;
    const row = rowOfBlock(b);
    if (!row || !isSendTime(row)) continue;
    const at = out.indexOf(row);
    if (at < 0) continue;
    out = out.slice(0, at) + sendTimePlaceholderRow(b.type) + out.slice(at + row.length);
  }
  return out;
}
